import React, { useCallback } from 'react';
import styles from './tv-guide.module.scss';
import testIds from '@smart-tv/e2e/shared/variables';
import {
  DEFAULT_FAVORITES_LIST,
  FavoritesList as IFavoritesList
} from '@smart-tv/contexts/favorites-lists-context/favorites-lists-context';

import Button from '@smart-tv/components/button/button';
import { Dialog, DialogItem } from '@smart-tv/components/dialog';

import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';

import TvGuideContext from '@smart-tv/pages/tv-guide/utils/tv-guide-context';
import TvGuideDayViewContext from '../utils/tv-guide-day-view-context';
import { SidebarContent, SidebarHeader, Sidebar } from '@smart-tv/components/dialog';
import NetworkErrorDialog from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { FAVORITES_FOCUS_KEY } from '@smart-tv/pages/tv-guide/utils/tv-guide-helpers';
import DialogContext from '@smart-tv/contexts/dialog-context/dialog-context';

enum FailedActionSource {
  GetFavorites = 'getFavorites',
  SelectFavorites = 'selectFavorites',
  OpenEditFavoriteLists = 'openEditFavoriteLists'
}

export const TvGuideFilterFavoritesDialog: React.FC = () => {
  const { favoritesDialog, setFavoritesDialog, setFavoritesChanged } =
    React.useContext(TvGuideContext);
  const {
    setActiveCenter,
    setActiveTVGuideFocusKey,
    setActiveChannelIndex,
    scrollYRef,
    setIsFavoritesListSelected
  } = React.useContext(TvGuideDayViewContext);

  const [usedFavoritesLists, setUsedFavoritesLists] = React.useState<IFavoritesList[]>([]);
  const [failedActionSource, setFailedActionSource] = React.useState<FailedActionSource | null>(
    null
  );
  const [lastSelectedFavoritesList, setLastSelectedFavoritesList] =
    React.useState<IFavoritesList | null>(null);

  const { selectedFavoritesList, onSelectFavoritesList, favoritLists, getFavoritesLists } =
    useFavoritesLists();
  const { handleOpen } = useFavoritesEdit();
  const { checkAndUpdateNetwork, setIsOffline, isOffline } = useNetwork();
  const { handleSetDialogFocusKey } = React.useContext(DialogContext);

  React.useEffect(() => {
    if (favoritLists) {
      const usedFavoritesLists = [
        DEFAULT_FAVORITES_LIST,
        ...favoritLists.filter(({ sids }) => sids.length > 0)
      ];

      setUsedFavoritesLists(usedFavoritesLists);
    }
  }, [favoritLists]);

  const getFavoritesAction = useCallback(async () => {
    setIsOffline(false);
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) {
      setFailedActionSource(null);
      getFavoritesLists();
    } else {
      setFailedActionSource(FailedActionSource.GetFavorites);
    }
  }, [checkAndUpdateNetwork, getFavoritesLists]);

  React.useEffect(() => {
    if (favoritesDialog) {
      getFavoritesAction();
    }
  }, [favoritesDialog, getFavoritesAction]);

  const handleCloseFavoritesDialog = useCallback(() => {
    setActiveCenter(undefined);
    setActiveTVGuideFocusKey(undefined);
    setActiveChannelIndex(0);
    setFavoritesDialog(false);
    scrollYRef.current?.scrollTo({
      top: 0
    });
    handleSetDialogFocusKey(FAVORITES_FOCUS_KEY);
  }, [
    scrollYRef,
    setActiveCenter,
    setActiveChannelIndex,
    setActiveTVGuideFocusKey,
    setFavoritesDialog,
    handleSetDialogFocusKey
  ]);

  const handleNetworkErrorDialogCloseAction = useCallback(() => {
    setIsOffline(false);
    if (failedActionSource === FailedActionSource.GetFavorites) {
      handleCloseFavoritesDialog();
    }
  }, [handleCloseFavoritesDialog, failedActionSource]);

  const handleSelectFavoritesList = useCallback(
    async (favoritesList: IFavoritesList) => {
      setLastSelectedFavoritesList(favoritesList);
      setIsOffline(false);
      const hasNetwork = await checkAndUpdateNetwork();
      if (hasNetwork) {
        setFailedActionSource(null);
        await onSelectFavoritesList(favoritesList);
        setIsFavoritesListSelected(true);
        setFavoritesChanged(true);
        handleCloseFavoritesDialog();
      } else {
        setFailedActionSource(FailedActionSource.SelectFavorites);
      }
    },
    [
      handleCloseFavoritesDialog,
      onSelectFavoritesList,
      setIsFavoritesListSelected,
      setFavoritesChanged,
      checkAndUpdateNetwork
    ]
  );

  const handleOpenEditFavoriteLists = useCallback(async () => {
    setIsOffline(false);
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) {
      setFailedActionSource(null);
      handleCloseFavoritesDialog();
      handleOpen();
    } else {
      setFailedActionSource(FailedActionSource.OpenEditFavoriteLists);
    }
  }, [handleCloseFavoritesDialog, handleOpen, checkAndUpdateNetwork]);

  const handleNetworkErrorDialogRetryAction = useCallback(async () => {
    if (failedActionSource === FailedActionSource.SelectFavorites && lastSelectedFavoritesList) {
      await handleSelectFavoritesList(lastSelectedFavoritesList);
      handleSetDialogFocusKey(FAVORITES_FOCUS_KEY);
    } else if (failedActionSource === FailedActionSource.OpenEditFavoriteLists) {
      await handleOpenEditFavoriteLists();
    } else {
      await getFavoritesAction();
    }
  }, [
    failedActionSource,
    lastSelectedFavoritesList,
    handleSelectFavoritesList,
    handleOpenEditFavoriteLists,
    getFavoritesAction,
    handleSetDialogFocusKey
  ]);

  if (!favoritesDialog) return null;

  return (
    <Dialog
      open={favoritesDialog}
      onClose={handleCloseFavoritesDialog}
      isSidebar={true}
      dialogContainerProps={{ 'data-testid': testIds.tvGuide.filter.favorites.dialog }}
    >
      <Sidebar>
        <SidebarHeader variant="h2" data-testid={testIds.tvGuide.filter.favorites.title}>
          Vælg kanalliste
        </SidebarHeader>
        <SidebarContent data-testid={testIds.tvGuide.filter.favorites.list}>
          {usedFavoritesLists.map((favoritesList, index) => (
            <DialogItem
              onClick={() => handleSelectFavoritesList(favoritesList)}
              data-testid={testIds.tvGuide.filter.favorites.item}
              key={index}
              isFocused={favoritesList.id === DEFAULT_FAVORITES_LIST.id}
              selected={favoritesList.id === selectedFavoritesList?.id}
              onEnterPress={() => handleSelectFavoritesList(favoritesList)}
            >
              {favoritesList.name}
            </DialogItem>
          ))}
          <Button
            className={styles['tv-guide-dialog__edit-favorites']}
            onClick={handleOpenEditFavoriteLists}
            data-testid={testIds.tvGuide.filter.favorites.edit}
          >
            Rediger kanallister
          </Button>
        </SidebarContent>
        {isOffline && (
          <NetworkErrorDialog
            onTryAgain={handleNetworkErrorDialogRetryAction}
            onClose={handleNetworkErrorDialogCloseAction}
          />
        )}
      </Sidebar>
    </Dialog>
  );
};

export default React.memo(TvGuideFilterFavoritesDialog);
